import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import { services } from "../../services";
import {
	DatasetStepEnum,
	type TDataRangeRequestObject,
} from "../../types/ledger.type";

import { LedgerActions } from "./ledgerSanitization.slice";

type TFileUploadObject = {
	file: File;
	datasetId: string;
	isStatic: boolean;
};

export const uploadFileThunk = createAsyncThunk(
	"ledgerSanitization/uploadFileThunk",
	async (
		fileUploadObject: TFileUploadObject,
		{ dispatch, rejectWithValue }
	) => {
		try {
			const formData = new FormData();
			const meta = {};
			formData.append("file", fileUploadObject.file);
			formData.append("data", JSON.stringify(meta));
			const data = await services.ledgerService.fileUpload(
				formData,
				fileUploadObject.datasetId,
				fileUploadObject.isStatic
			);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue({
				...error.response.data,
				msg: `Error occurred: ${error.response.data.error_code} - ${error.response.data.msg}`,
			});
		}
	}
);

export const getGeneralMetaDataThunk = createAsyncThunk(
	"ledgerSanitization/getGeneralMetaDataThunk",
	async (_, { dispatch, rejectWithValue }) => {
		try {
			const data =
				await services.ledgerService.getDatasetGeneralMeta();
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue({
				...error.response.data,
				msg: `Error occurred: ${error.response.data.error_code} - ${error.response.data.msg}`,
			});
		}
	}
);

export const getDatasetMetaThunk = createAsyncThunk(
	"ledgerSanitization/getDatasetMetaThunk",
	async (
		datasetId: string,
		{ dispatch, rejectWithValue }
	) => {
		try {
			const data =
				await services.ledgerService.getDatasetMeta(
					datasetId
				);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			const backendError = error.response?.data;
			const backendMessage =
				typeof backendError === "string"
					? backendError
					: (backendError?.msg ??
						backendError?.message ??
						backendError?.error);
			return rejectWithValue({
				...(typeof backendError === "object" &&
				backendError !== null
					? backendError
					: {}),
				msg:
					backendMessage ??
					error.message ??
					"Unable to load dataset metadata.",
				status: error.response?.status,
			});
		}
	}
);

export const getStaticDataRangeThunk = createAsyncThunk(
	"ledgerSanitization/getStaticDataRange",
	async (
		datasetId: string,
		{ dispatch, rejectWithValue }
	) => {
		try {
			const data =
				await services.ledgerService.getStaticDataRange(
					datasetId
				);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue({
				...error.response.data,
				msg: `Error occurred: ${error.response.data.msg}`,
				status: error.response.status,
			});
		}
	}
);

export const getPerfDataRangeThunk = createAsyncThunk(
	"ledgerSanitization/getPerfDataRange",
	async (
		datasetId: string,
		{ dispatch, rejectWithValue }
	) => {
		try {
			const data =
				await services.ledgerService.getPerfDataRange(
					datasetId
				);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue({
				...error.response.data,
				msg: `Error occurred: ${error.response.data.msg}`,
				status: error.response.status,
			});
		}
	}
);

export const getStaticDataFromDataRange = createAsyncThunk(
	"ledgerSanitization/getStaticDataFromDataRange",
	async (
		reqObj: TDataRangeRequestObject,
		{ dispatch, rejectWithValue }
	) => {
		try {
			const data =
				await services.ledgerService.updateStaticDataRange(
					{
						is_step_confirmed: reqObj.isStepConfirmed,
						sheet_name: reqObj.sheet,
						header_range: reqObj.headerRange,
						body_range: reqObj.dataRange,
					},
					reqObj.datasetId
				);
			dispatch(
				LedgerActions.updateRangeSelection({
					sheet: reqObj.sheet,
					headerRange: reqObj.headerRange,
					dataRange: reqObj.dataRange,
					isStatic: true,
				})
			);
			if (reqObj.isReload) {
				toast.success("Data range preview updated.");
			}
			if (reqObj.isStepConfirmed) {
				dispatch(
					LedgerActions.updateSanitizationSubmissionStatus({
						currentStep:
							DatasetStepEnum.STEP_STATIC_DATA_RANGE,
						submissionCreated: true,
						isSubmissionSuccessful: true,
					})
				);
				return {
					table: null,
					...data,
				};
			} else {
				dispatch(
					LedgerActions.updateSanitizationSubmissionStatus({
						currentStep:
							DatasetStepEnum.STEP_STATIC_DATA_RANGE,
						submissionCreated: false,
						isSubmissionSuccessful: false,
					})
				);
			}
			return { table: data };
		} catch (error: any) {
			console.error("redux error", error);
			dispatch(
				LedgerActions.updateSanitizationSubmissionStatus({
					currentStep:
						DatasetStepEnum.STEP_STATIC_DATA_RANGE,
					submissionCreated: true,
					isSubmissionSuccessful: false,
					error: error.response.data,
				})
			);
			return rejectWithValue({
				...error.response,
				msg: `Error occurred: ${error.response.data.msg}`,
			});
		}
	}
);

export const getPerfDataFromDataRange = createAsyncThunk(
	"ledgerSanitization/getPerfDataFromDataRange",
	async (
		reqObj: TDataRangeRequestObject,
		{ dispatch, rejectWithValue }
	) => {
		try {
			const data =
				await services.ledgerService.updatePerfDataRange(
					{
						is_step_confirmed: reqObj.isStepConfirmed,
						sheet_name: reqObj.sheet,
						header_range: reqObj.headerRange,
						body_range: reqObj.dataRange,
					},
					reqObj.datasetId
				);
			dispatch(
				LedgerActions.updateRangeSelection({
					sheet: reqObj.sheet,
					headerRange: reqObj.headerRange,
					dataRange: reqObj.dataRange,
					isStatic: false,
				})
			);
			if (reqObj.isReload) {
				toast.success("Data range preview updated.");
			}
			if (reqObj.isStepConfirmed) {
				dispatch(
					LedgerActions.updateSanitizationSubmissionStatus({
						currentStep:
							DatasetStepEnum.STEP_PERF_DATA_RANGE,
						submissionCreated: true,
						isSubmissionSuccessful: true,
					})
				);
				return {
					table: null,
					...data,
				};
			} else {
				dispatch(
					LedgerActions.updateSanitizationSubmissionStatus({
						currentStep:
							DatasetStepEnum.STEP_PERF_DATA_RANGE,
						submissionCreated: false,
						isSubmissionSuccessful: false,
					})
				);
			}
			return { table: data };
		} catch (error: any) {
			console.error("redux error", error);
			dispatch(
				LedgerActions.updateSanitizationSubmissionStatus({
					currentStep: DatasetStepEnum.STEP_PERF_DATA_RANGE,
					submissionCreated: true,
					isSubmissionSuccessful: false,
					error: error.response.data,
				})
			);
			return rejectWithValue({
				...error.response,
				msg: `Error occurred: ${error.response.data.msg}`,
			});
		}
	}
);

export const fetchDatasetSpecsQuestionsThunk =
	createAsyncThunk(
		"ledgerSanitization/fetchDatasetSpecsQuestionsThunk",
		async (datasetId: string, { rejectWithValue }) => {
			try {
				const data =
					await services.ledgerService.getDatasetSpecsQuestions(
						datasetId
					);
				return data;
			} catch (error: any) {
				console.error("redux error", error);
				return rejectWithValue({
					...error.response.data,
					msg: `Error occurred: ${error.response.data.msg}`,
				});
			}
		}
	);

export const rerunDatasetStatsThunk = createAsyncThunk(
	"ledgerSanitization/rerunDatasetStatsThunk",
	async (
		reqObj: {
			datasetId: string;
			dashboardParams?: Record<string, any>;
		},
		{ rejectWithValue }
	) => {
		try {
			const data =
				await services.ledgerService.rerunDatasetStats(
					reqObj.datasetId,
					reqObj.dashboardParams ?? {}
				);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue(
				error?.response?.data ?? error?.data ?? {}
			);
		}
	}
);

export const fetchDatasetStatsThunk = createAsyncThunk(
	"ledgerSanitization/fetchDatasetStatsThunk",
	async (datasetId: string, { rejectWithValue }) => {
		try {
			const data =
				await services.ledgerService.getDatasetStats(
					datasetId
				);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			const responseData =
				error?.response?.data ?? error?.data ?? {};
			return rejectWithValue({
				...responseData,
				status:
					error?.response?.status ?? error?.status ?? null,
				statusText:
					error?.response?.statusText ??
					error?.statusText ??
					null,
				msg:
					responseData.msg ??
					responseData.error_message ??
					responseData.message ??
					error?.message ??
					"Unable to load review data.",
			});
		}
	}
);

export const confirmDatasetStatsThunk = createAsyncThunk(
	"ledgerSanitization/confirmDatasetStatsThunk",
	async (datasetId: string, { rejectWithValue }) => {
		try {
			const data =
				await services.ledgerService.confirmDatasetStats(
					datasetId
				);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue({
				...error.response.data,
				msg: `Error occurred: ${error.response.data.msg}`,
			});
		}
	}
);
